import { ReactNode, RefObject, useLayoutEffect } from 'react';

// `width: fit-content` resolves against the unwrapped single-line size, so once a
// message wraps, the box fills its container and `margin-inline: auto` has nothing
// left to center. Instead, clamp the element to its longest rendered line so the auto
// margins can center the left-aligned text block.
//
// The available width isn't stable at mount: inline confirms render inside a popover
// that is positioned/sized after mount, so a one-shot measurement locks in the wrong
// (wider) width. A ResizeObserver on the parent re-measures once layout settles.
export function useFitLongestLine(ref: RefObject<HTMLElement>, content: ReactNode) {
  useLayoutEffect(() => {
    const element = ref.current;
    const parent = element?.parentElement;
    if (!element || !parent) {
      return;
    }
    const measure = () => setLongestLineWidth(element);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    return () => observer.disconnect();
  }, [ref, content]);
}

function setLongestLineWidth(element: HTMLElement) {
  // `Range.getClientRects()` is unreliable for the longest-line width: with
  // `white-space: pre-line` and soft-wrapping it reports full-width boxes for some
  // lines. Instead, binary-search the narrowest width that doesn't add a line — that
  // width equals the longest rendered line.
  const parent = element.parentElement;
  if (!parent) {
    return;
  }
  element.style.width = '100vw';
  const parentStyle = getComputedStyle(parent);
  const parentPadding = parseFloat(parentStyle.paddingLeft) + parseFloat(parentStyle.paddingRight);
  const available = parent.clientWidth - parentPadding;
  element.style.width = `${available}px`;
  const fullHeight = element.clientHeight;
  if (available === 0 || fullHeight === 0) {
    return;
  }

  let tooNarrow = 0; // heights grow (extra line) at or below this width
  let wideEnough = available; // no extra line at or above this width
  while (wideEnough - tooNarrow > 1) {
    const mid = (tooNarrow + wideEnough) / 2;
    element.style.width = `${mid}px`;
    if (element.clientHeight > fullHeight) {
      tooNarrow = mid;
    } else {
      wideEnough = mid;
    }
  }
  // Clamp to the longest line so `margin-inline: auto` can center the block. When the
  // text already fills the width this equals `available`, leaving the block full-width.
  element.style.width = `${Math.ceil(wideEnough)}px`;
}
